import type { SliderProps as UnstyledSliderProps } from "@mui/material";
import { Slider as UnstyledSlider, styled } from "@mui/material";
import type { ComponentPropsWithRef } from "react";

export interface MinMaxProps extends Pick<UnstyledSliderProps, "min" | "max"> {
  enabledMin?: number;
  enabledMax?: number;
}

/* min/max props are required for this styled component, but not for the implementation */
interface SliderProps
  extends Omit<UnstyledSliderProps, keyof MinMaxProps>, Required<MinMaxProps> {}

export const Slider = styled(UnstyledSlider, {
  shouldForwardProp: (
    propName: keyof SliderProps,
  ): propName is keyof ComponentPropsWithRef<typeof UnstyledSlider> =>
    !["enabledMin", "enabledMax"].includes(propName),
})<SliderProps>(({ theme, min, max, enabledMin, enabledMax }) => ({
  height: theme.spacing(2.5),

  "& > .MuiSlider-markLabel": {
    color: theme.palette.grey[500],
    fontFamily: theme.typography.fontFamilyMono,
  },

  "& > .MuiSlider-rail": {
    color: theme.palette.grey[300],
  },

  // disabled zones
  "&::before": {
    content: '" "',
    background: `repeating-linear-gradient(
      45deg,
      /* use grey[200] from 0 to 4px */
      ${theme.palette.grey[200]},
      ${theme.palette.grey[200]} ${theme.spacing(0.5)},
      /* use transparent from 4px to 8px */
      transparent ${theme.spacing(0.5)},
      transparent ${theme.spacing(1)}
    )`,
    opacity: 0.5,
    position: "absolute",
    height: "100%",
    width: "100%",
    // draw polygons clockwise to avoid negation on overlap
    clipPath: `shape(
      from 0 0,
      /* min zone */
      move to ${(100 * (enabledMin - min)) / (max - min)}% 0, vline to 100%, hline to 0, vline to 0, close,
      /* max zone */
      move to ${(100 * (enabledMax - min)) / (max - min)}% 100%, vline to 0, hline to 100%, vline to 100%, close,
    )`,
  },
}));
